import { getAiService } from '../functions/aiService.js'

// AiPickerRow — single row inside the AI parameters dropdown (mounted by
// CanvasPromptTextarea.ParamsBtn → openDropdown({ content: AiModePickerBody })).
//
// EXTENDS the house menu-row recipe `MenuPanelItem` so it inherits the same
// borderless button semantics, radius, coarse-pointer 44pt min-height and
// theme-aware :hover wash every other workspace dropdown uses — then layers a
// leading icon + two-line body + trailing check on top.
//
// Per-row state shape (fed via childrenAs:'state' from the parent Options
// factory):
//   { key, label, sub, icon, active, authMode? }
//
// `icon`     — brand Icon glyph name shown as the leading visual anchor.
// `active`   — true for the currently-selected mode; drives the house
//              selected wash (`.isActive`) + the trailing green check.
// `authMode` — flips the row from picking a provider mode to picking an
//              authorization mode (ask / auto). Both picker sections reuse
//              this one component so visual consistency is automatic.
//
// On click:
//   - provider rows  → sdk.ai.setMode(state.key)
//   - authMode rows  → sdk.ai.setAuthMode(state.key)
// Then writes _aiModePickerTick (an arbitrary integer) to nudge the picker's
// children: factories to re-evaluate so the active wash/check move immediately.
export const AiPickerRow = {
  extends: 'MenuPanelItem',
  flow: 'x',
  gap: 'A',
  // A touch more vertical room than the one-line MenuPanelItem so the
  // label + sub two-liner breathes; horizontal padding matches the house row.
  padding: 'X2 Z',
  // MenuPanelItem is nowrap (single-line menu items); the sub-line here can
  // wrap on a narrow panel, so allow normal wrapping.
  whiteSpace: 'normal',

  // Active row — the HOUSE selected wash. Same base grays as the `overlay`
  // design-system token (dark `gray14` / light `gray2`), a step stronger in
  // alpha than the inherited MenuPanelItem :hover — i.e. the CanvasViewNav
  // active level (~10% wash), NOT `overlayStrong` (near-opaque, too heavy for
  // a row). Driven by a reactive `isActive` predicate → toggles the
  // `.isActive` class, the reliably-reactive path (plain style factories
  // freeze at mount). The trailing green check stays as a secondary,
  // belt-and-suspenders cue.
  isActive: (el, s) => !!s.active,
  // The active row already stays visibly selected on hover WITHOUT a compound
  // `.isActive:hover`: a case block applies its flat props as INLINE styles, and
  // `@light` sub-blocks compile to `[data-theme="light"] &` with `!important` —
  // both outrank MenuPanelItem's plain `:hover` class rule. An extra nested
  // `':hover'` here was therefore unreachable in both themes, and its own
  // `@light` child compiled to the non-matching selector `._cls:hover @light`
  // (the `[data-theme]` transform only applies at the first nesting level), so
  // it emitted invalid CSS for nothing. Dropped.
  '.isActive': {
    background: 'gray14.1',
    '@light': { background: 'gray2.06' }
  },

  onClick: (e, el, s) => {
    e.preventDefault()
    e.stopPropagation()
    const ai = getAiService()
    if (!ai) return
    try {
      if (s.authMode) ai.setAuthMode(s.key)
      else ai.setMode(s.key)
      // Bump a tick so the picker's children: factories re-evaluate and the
      // active wash/check move to the newly-active row. Without this the
      // dropdown stays on its initial render until closed and reopened.
      const root = el.state?.root
      if (root?.update) {
        const next = (root._aiModePickerTick || 0) + 1
        root.update({ _aiModePickerTick: next })
      }
    } catch (err) {
      console.warn('[AiPickerRow] mode change failed:', err?.message || err)
    }
  },

  // Leading brand Icon — the per-option visual anchor. Muted (`caption`) by
  // default, brightening to `title` on the active row via its own `.isActive`
  // block (mirrors CanvasViewNav's active child icon). `color` drives the
  // glyph fill through the Icon's `currentColor`.
  // `if:` — this slot had NO gate at all, so an option that names no glyph
  // painted a VISIBLE `noIcon` placeholder and warned
  // `[Icon] Unknown icon name "Icon"` (the built-in falls through to the
  // element KEY when `name`/`icon` answers undefined). Every feed shipping
  // today carries an icon, so nothing changes on screen now; the gate is what
  // keeps the next feed honest. Same recipe as AiMenu's AiOptionRow and
  // ContextDropdownItem.
  Icon: {
    if: (el, s) => !!s?.icon,
    name: (el, s) => s.icon,
    boxSize: 'Z1',
    color: 'caption',
    flexShrink: '0',
    isActive: (el, s) => !!s.active,
    '.isActive': { color: 'title' }
  },

  Body: {
    flow: 'y',
    gap: 'V',
    flex: '1',
    minWidth: '0',
    // Pin a tight line-height — the panel/theme otherwise inherits a loose
    // ~27px line-height that made every row tall and label/sub float apart.
    lineHeight: '1.3',
    Label: {
      tag: 'span',
      fontWeight: '700',
      color: 'title',
      text: (el, s) => el.call('polyglot', s.label || '')
    },
    Sub: {
      tag: 'span',
      fontSize: 'Y',
      color: 'caption',
      lineHeight: '1.4',
      text: (el, s) => el.call('polyglot', s.sub || '')
    }
  },

  // Trailing checkmark — a second `Icon` instance via key-based auto-extend
  // (the `_1` suffix is ignored for extend resolution, so no `extends: 'Icon'`
  // needed). Declared AFTER Body, which takes flex:1 and pushes it to the
  // right edge, so every label stays left-aligned regardless of active state;
  // the glyph shows only on the active row.
  Icon_1: {
    name: 'checkmark',
    boxSize: 'Z',
    color: 'green',
    flexShrink: '0',
    hide: (el, s) => !s.active
  }
}
